import { authFetch, isAuthorizedInUrl } from '~/services/fetch'
import { LatLngExpression, LatLng } from 'leaflet'
import {
  parseAccessTokenInUrl,
  parseHostname,
} from '~/features/Map/utils/parsers'
import axios from 'axios'
import type { createDispatchByDeviceProps, IncidentResponse } from '../types'
import { count } from 'console'

export async function storeUserViewPort({
  latitude,
  longitude,
  zoom,
  label,
  favorite,
}) {
  try {
    const response = await authFetch({
      url: '/map-viewport/store',
      method: 'POST',
      data: {
        latitude,
        longitude,
        zoom,
        label,
        favorite,
      },
    })

    return response.data
  } catch (error) {
    throw error
  }
}

export async function getMapViewPort() {
  try {
    const response = await authFetch({
      url: '/map-viewport/find-by-user',
      method: 'GET',
    })

    return response.data
  } catch (error) {
    throw error
  }
}

export async function requestDeviceMarkerUpdate(deviceId) {
  try {
    const response = await authFetch({
      url: '/device/update-map-location-data',
      method: 'POST',
      data: { device_id: deviceId },
    })

    return response.data
  } catch (error) {
    throw error
  }
}

export async function getCenterService(): Promise<{
  readonly coordinates: LatLngExpression
  readonly zoom: number
}> {
  try {
    const response = await authFetch({
      url: '/service/get-central-coordinates',
      method: 'POST',
    })

    return response.data
      ? { coordinates: response.data.coordinates, zoom: response.data.zoom }
      : { coordinates: [-4.968465458391433, -39.01640317712328], zoom: 18 }
  } catch (error) {
    throw error
  }
}

export async function getMapUrlService({ darkMode = false }) {
  try {
    const response = await authFetch({
      url: '/service/get-map-url',
      method: 'POST',
      data: { darkMode },
    })

    const validUrl = response.data.token
      ? parseAccessTokenInUrl(response.data.url, response.data.token)
      : response.data.url

    try {
      if (
        await isAuthorizedInUrl({
          url: response.data.statusUrl ?? parseHostname(validUrl),
          timeout: 3000,
        })
      ) {
        return { ...response, url: validUrl }
      } else {
        return (
          await authFetch({
            url: '/service/get-map-url',
            method: 'POST',
            data: { darkMode, fallback: true },
          })
        ).data
      }
    } catch (error) {
      throw error
    }
  } catch (error) {
    throw error
  }
}

export async function getAreaPlanService() {
  try {
    const response = await authFetch({
      url: '/area-plan/areas',
      method: 'GET',
    })

    return response.data
  } catch (error) {
    throw error
  }
}

export async function getCameraService() {
  try {
    const response = await authFetch({
      url: '/camera',
      method: 'GET',
    })

    return response.data
  } catch (error) {
    throw error
  }
}

export async function createLiveOperation(cameraIndex: string) {
  try {
    const response = await authFetch({
      url: '/camera/show-in-client',
      method: 'POST',
      data: {
        cameraIndex,
      },
    })

    return response.data
  } catch (error) {
    throw error
  }
}

export async function getMarkersCameraService() {
  try {
    const { data: camerasPayload } = await authFetch({
      url: '/camera/find-for-map',
      method: 'GET',
    })
    const cameras = Object.keys(camerasPayload).map((cameraIdx) => {
      return {
        id: cameraIdx,
        markerId: `camera-${cameraIdx}`,
        position: new LatLng(
          camerasPayload[cameraIdx].latitude,
          camerasPayload[cameraIdx].longitude
        ),
        cameras: camerasPayload[cameraIdx]['cameras'],
      }
    })

    return cameras
  } catch (error) {
    throw error
  }
}

export async function getMarkersInterestPointService() {
  try {
    const response = await authFetch({
      url: '/interest-point/all-for-map',
      method: 'POST',
      data: {
        only_visible_to_dispatch: window.location.pathname === '/dispatch' ? true : false
      }
    })

    const result = response.data
      .filter((item) => item.latitude != null && item.longitude != null)
      .reduce((acc, interestPoint) => {
        const {
          id,
          /* eslint-disable camelcase */
          interest_point_type,
          interest_point_subtype,
          latitude,
          longitude,
          location,
          validated_at,
          expiration_date,
          revision_date,
          name,
          medias,
          notes,
        } = interestPoint

        // eslint-disable-next-line functional/immutable-data
        acc[id] = {
          id: id,
          markerId: `interest-point-${id}`,
          location: location ?? '',
          position: new LatLng(latitude, longitude),
          typeName: interest_point_type?.name ?? null,
          subtypeName: interest_point_subtype?.name ?? null,
          isCriminal: interest_point_type?.is_criminal,
          isCurrent: false,
          validatedAt: validated_at,
          revisionDate: revision_date,
          expirationDate: expiration_date,
          nameInterestPoint: name,
          medias: medias,
          notes: notes
        }

        return acc
      }, {})

    return result
  } catch (error) {
    throw error
  }
}

export async function createDispatchByDevice({
  deviceId,
  latitude,
  longitude,
  typeId,
  subtypeId,
  comment,
  name,
  phone,
  entryOriginId,
}: createDispatchByDeviceProps) {
  try {
    const response = authFetch({
      url: '/dispatch/create',
      method: 'POST',
      data: {
        device_id: deviceId,
        latitude: latitude,
        longitude: longitude,
        type: typeId,
        subtype: subtypeId,
        comment: comment,
        name: name,
        phone: phone,
        entry_origin_id: entryOriginId,
      },
    })
    return response
  } catch (error) {
    throw error
  }
}

export async function getMarkersPrevisionService({ start_date, finish_date, criticalityLevel, prediction_model_id }: { readonly start_date: any; readonly finish_date: any; readonly criticalityLevel: number; readonly prediction_model_id?: number | null }) {
  try {
    const payload: any = {
      start_date: start_date,
      finish_date: finish_date,
      criticality_level: criticalityLevel,
    }
    if (prediction_model_id != null) payload.prediction_model_id = prediction_model_id

    const { data } = await authFetch({
      url: `/occurrence-predictions`,
      method: 'POST',
      data: payload,
    })

    const result = data.filter((item) => item.latitude != null && item.longitude != null && item.group)
      // const result = data.filter((item) => item.latitude != null && item.longitude != null)
      .reduce((acc, prediction) => {
        const {
          latitude,
          longitude,
          group,
        } = prediction;

        // eslint-disable-next-line functional/immutable-data
        acc[`${latitude}${longitude}`] = {
          id: `${latitude}${longitude}`,
          markerId: `prediction-${`${latitude}${longitude}`}`,
          position: new LatLng(
            latitude,
            longitude
          ),
          group: group
        }

        return acc;
      }, {})

    return result
  } catch (error) {
    throw error
  }
}

export async function getMarkersDispatchFilteredPrevisionsService({ start_date, finish_date, criticalityLevel, prediction_model_id }: { readonly start_date: any; readonly finish_date: any; readonly criticalityLevel: number; readonly prediction_model_id?: number | null }) {
  try {
    const payload: any = {
      start_date: start_date,
      finish_date: finish_date,
      criticality_level: criticalityLevel,
    }
    if (prediction_model_id != null) payload.prediction_model_id = prediction_model_id

    const { data } = await authFetch({
      url: '/dispatch/for-prediction-map',
      method: 'POST',
      data: payload,
    });

    const result = data
      .filter((item) => item.occurrence)
      .filter((item) => item.occurrence.latitude != null && item.occurrence.longitude != null)
      .reduce((acc, dispatch) => {
        const {
          id: dispatchId,
          code,
          occurrence,
          type,
          subtype,
          status,
          /* eslint-disable camelcase */
          dispatched_at
        } = dispatch;

        // eslint-disable-next-line functional/immutable-data
        acc[dispatchId] = {
          id: dispatchId,
          markerId: `dispatch-${dispatchId}`,
          code: code,
          location: occurrence.location ?? '',
          position: new LatLng(
            occurrence.latitude,
            occurrence.longitude
          ),
          typeName: type?.name ?? null,
          subtypeName: subtype?.name ?? null,
          /* eslint-disable camelcase */
          dispatchedAt: dispatched_at ?? null,
          status: status.id,
          isCurrent: false,
        }

        return acc;
      }, {})

    return result

  } catch (error) {
    throw error
  }
}

export async function getMarkersDispatchFilteredHistoricalService({ dateTime }) {
  try {
    const { data } = await authFetch({
      url: '/dispatch/ongoing-at-the-date',
      method: 'POST',
      data: {
        date_time: dateTime
      },
    });

    const result = data
      .filter((item) => item.occurrence)
      .filter((item) => item.occurrence.latitude != null && item.occurrence.longitude != null)
      .reduce((acc, dispatch) => {
        const {
          id: dispatchId,
          code,
          occurrence,
          type,
          subtype,
          status_at_moment,
          /* eslint-disable camelcase */
          dispatched_at
        } = dispatch;

        // eslint-disable-next-line functional/immutable-data
        acc[dispatchId] = {
          id: dispatchId,
          markerId: `dispatch-${dispatchId}`,
          code: code,
          location: occurrence.location ?? '',
          position: new LatLng(
            occurrence.latitude,
            occurrence.longitude
          ),
          typeName: type?.name ?? null,
          subtypeName: subtype?.name ?? null,
          /* eslint-disable camelcase */
          dispatchedAt: dispatched_at ?? null,
          status: status_at_moment.id,
          isCurrent: false,
        }

        return acc;
      }, {})

    return result

  } catch (error) {
    throw error
  }
}

export async function getMarkersDeviceFilteredHistoricalService({ dateTime }) {
  try {
    const { data } = await authFetch({
      url: '/device/ongoing-at-the-date',
      method: 'POST',
      data: {
        date_time: dateTime
      },
    });

    const result = {}

    data
      .filter((device) => device?.latitude && device?.longitude)
      .map((device, index) => {
        if (
          device?.device_id &&
          device?.latitude &&
          device?.longitude &&
          device?.device_status_id &&
          device?.identifier
        ) {
          // eslint-disable-next-line functional/immutable-data
          result[Number(device?.device_id)] = {
            id: device?.device_id,
            markerId: `device-${device?.device_id}`,
            code: device?.code ?? '',
            position: new LatLng(device.latitude, device.longitude),
            status: device?.device_status_id ?? '10',
            identifier: device?.identifier,
            speed: device?.speed ? device?.speed : '0',
            plate: device?.plate ? device?.plate : '',
            date: device?.date ? device?.date : null,
            groupId: device?.group_id,
            isCurrent: false,
            deviceType: device?.device_type ? device?.device_type : 'car',
            dateLastConnection: device.date_last_connection ? device.date_last_connection : ''
          }
        }
      })

    return result

  } catch (error) {
    throw error
  }
}

export async function getIncidentById({ incidentId }): Promise<IncidentResponse> {
  try {
    const { data } = await authFetch({
      url: `/incident/${incidentId}`,
      method: 'GET',
    })

    return data
  } catch (error) {
    throw error;
  }
}

export async function getMarkersFilteredHeatMapDispatchsService({
  agencyId,
  deviceId,
  dispatchGroupId,
  category,
  type,
  subtype,
  dateStart,
  dateFinish,
  payload,
  operationId,
  consummated,
  statusIds,
  cityIds,
  districtIds,
  groupSetId,
  location,
  closedStatusClassId,
  closedStatusId,
  qualifierOptions,
  operationUnitId,
  locationAccuracy,
  phone,
  geographicalArea,
  requester,
  userId,
}) {
  try {
    const { data } = await authFetch({
      url: '/statistics/dispatches-pinned-by-coordinates',
      method: 'POST',
      data: {
        agencyId: agencyId,
        deviceId: deviceId,
        dispatchGroupIds: dispatchGroupId,
        categoryIds: category,
        typeIds: type,
        subtypeIds: subtype,
        startDate: dateStart,
        finishDate: dateFinish,
        excludeClosed: payload.exludeClosed,
        withSolvedThirdParty: payload.withSolvedThirdParty,
        operationId: operationId,
        consummated: consummated,
        statusIds: statusIds,
        cityIds: cityIds,
        districtIds: districtIds,
        groupSetId: groupSetId,
        location: location,
        closedStatusClassId: closedStatusClassId,
        closedStatusId: closedStatusId,
        qualifierOptions: qualifierOptions,
        operationUnitId: operationUnitId,
        locationAccuracy: locationAccuracy,
        phone: phone,
        geographicAreas: geographicalArea,
        name: requester,
        userId: userId,
      }
    })

    const result = {}

    Object.values(data).map((item: any) => {
      if (item.length == 1) {
        // eslint-disable-next-line functional/immutable-data
        result[`${item[0].latitude}-${item[0].longitude}`] = {
          id: item[0].id,
          occurrenceId: item[0].id,
          markerId: `dispatch-${item[0].id}`,
          address: item[0].address,
          code: item[0].code,
          dispatchedAt: item[0].dispatched_at,
          status: item[0].status.id,
          statusObj: item[0].status,
          dispatchGroupObj: item[0].dispatch_group,
          agencyObj: item[0].agency,
          position: new LatLng(item[0].latitude, item[0].longitude),
          location: item[0].location ?? '',
          name: item[0].name,
          typeName: item[0].type.name,
          subtypeName: item[0].subtype?.name ?? null,
          count: 1
        }
      } else {
        // eslint-disable-next-line functional/immutable-data
        result[`${item[1].latitude}-${item[1].longitude}`] = {
          id: `${item[1].latitude}-${item[1].longitude}`,
          codeList: item.map((code) => code.code),
          markerId: `position-${item[1].latitude}-${item[1].longitude}`,
          position: new LatLng(item[1].latitude, item[1].longitude),
          count: item.length,
          dispatches: item
        }
      }
    })

    return result
    // const result = data
    //   .filter((itemFiltered) => itemFiltered?.latitude && itemFiltered?.longitude)
    //   .reduce((acc, filteredData) => {
    //     filteredData.dispatches?.forEach((dispatch) => {
    //       // eslint-disable-next-line functional/immutable-data
    //       acc[dispatch.id] = {
    //         id: dispatch.id,
    //         occurrenceId: filteredData.id,
    //         markerId: `dispatch-${dispatch.id}`,
    //         address: dispatch.address,
    //         code: dispatch.code,
    //         dispatchedAt: dispatch.dispatched_at,
    //         status: dispatch.status.id,
    //         statusObj: dispatch.status,
    //         dispatchGroupObj: dispatch.dispatch_group,
    //         agencyObj: dispatch.agency,
    //         position: new LatLng(filteredData.latitude, filteredData.longitude),
    //         location: filteredData.location ?? '',
    //         name: filteredData.name,
    //         typeName: filteredData.type.name,
    //         subtypeName: filteredData.subtype?.name ?? null,
    //       }
    //     })
    //     return acc
    //   }, {})

  } catch (error) {
    throw error
  }
}